import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MapEngine } from "./core/MapEngine";
import { darkMapConfig, type MapConfig } from "./core/MapConfig";
import { MapContext } from "./MapContext";
import { useMapConfig, useMapSettings } from "../hooks/useMapQuery";

import "maplibre-gl/dist/maplibre-gl.css";
import { cn } from "../lib/utils";
import { useTheme } from "../providers/ThemeProvider";

export const MapView = () => {
  const { data, isLoading } = useMapConfig();
  const { theme } = useTheme();
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapEngineRef = useRef<MapEngine | null>(null);

  const mapEngine = MapEngine.getInstance();

  useLayoutEffect(() => {
    if (!containerRef.current || !data) return;

    const { longitude, latitude } = data.position;

    const styleURL = data.showSatellite
      ? data.satelliteMapUrl
      : data.darkMapUrl;

    const _config: MapConfig = {
      ...darkMapConfig,
      style: styleURL ?? "",
      center: [longitude ?? 0, latitude ?? 0],
      zoom: data.zoom,
    };

    mapEngineRef.current = mapEngine;
    const _map = mapEngine.initialize(containerRef.current, _config);

    _map.once("idle", () => {
      setTimeout(() => setIsReady(true), 50);
    });

    return () => {
      mapEngine.remove();
      containerRef.current = null;
    };
  }, [data]);

  useEffect(() => {
    const _map = mapEngine.getMap();

    if (!data || !_map || data.showSatellite) return;

    if (theme === "light") {
      setIsReady(false);
      _map.setStyle(data.lightMapUrl);
      _map.once("idle", () => {
        setTimeout(() => setIsReady(true), 50);
      });
    }

    if (theme === "dark") {
      setIsReady(false);
      _map.setStyle(data.darkMapUrl);
      _map.once("idle", () => {
        setTimeout(() => setIsReady(true), 50);
      });
    }
    if (theme === "system") {
      setIsReady(false);
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      _map.setStyle(isDark ? data.darkMapUrl : data.lightMapUrl);
      _map.once("idle", () => {
        setTimeout(() => setIsReady(true), 50);
      });
    }
  }, [theme, data, isLoading]);

  if (isLoading) return <>Loading...</>;

  return (
    <MapContext.Provider value={mapEngineRef.current}>
      <div
        ref={containerRef}
        className={cn(
          "w-full h-full",
          isReady ? "animate-in fade-in duration-300" : "opacity-0",
        )}
      />
    </MapContext.Provider>
  );
};
